import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Eye, Tag, ArrowRight } from 'lucide-react';
import { formatDate } from '../../utils/format/date';
import type { BlogPost } from '../../types/blog';
import { motion } from 'framer-motion';

interface BlogCardProps {
  post: BlogPost;
}

export function BlogCard({ post }: BlogCardProps) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
    >
      {/* Featured Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={post.featuredImage || 'https://images.unsplash.com/photo-1639322537228-f710d846310a'}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-4 left-4">
          <span 
            className="px-3 py-1 rounded-full text-xs font-medium text-white"
            style={{ backgroundColor: post.category.color }}
          >
            {post.category.name}
          </span>
        </div>
        {post.isAutoGenerated && (
          <div className="absolute top-4 right-4">
            <span className="px-2 py-1 bg-blue-500 text-white rounded-full text-xs font-medium">
              AUTO
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(post.publishedAt)}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>{post.readTime} min read</span>
          </div>
          {post.views && (
            <div className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              <span>{post.views.toLocaleString()}</span>
            </div>
          )}
        </div>

        <h3 className="text-xl font-bold mb-3 line-clamp-2 hover:text-emerald-600 transition-colors">
          <Link to={`/blog/${post.slug}`}>
            {post.title}
          </Link>
        </h3>

        <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
          {post.excerpt}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600
                       rounded-full text-xs cursor-default"
            >
              <Tag className="h-3 w-3" />
              #{tag}
            </span>
          ))}
          {post.tags.length > 3 && (
            <span className="text-xs text-gray-400">
              +{post.tags.length - 3} more
            </span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <span className="text-sm text-gray-500">
            By {post.author}
          </span>
          <Link
            to={`/blog/${post.slug}`}
            className="text-emerald-600 hover:text-emerald-700 font-medium text-sm 
                     flex items-center gap-1 group"
          >
            Read More 
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}